import { useState } from "react";
import DashLayout from "@/layouts/DashLayout";
import PageTitle from "@/components/PageTitle";
import InfoRestaurant from "@/components/parametres/tabs/InfoRestaurant";
import InfoFranchise from "@/components/parametres/tabs/InfoFranchise";
import ListeUtilisateurs from "@/components/parametres/tabs/ListeUtilisateurs";
import ListeFranchiseOwners from "@/components/parametres/tabs/ListeFranchiseOwners";
import ListeRestaurantManagers from "@/components/parametres/tabs/ListeRestaurantManagers";
import EditableAvatar from "@/components/EditableAvatar";
import { ButtonAdd } from "@/components/ui/button";
import { useToggle } from "@/lib/hooks";
import AddUserModal from "@/components/parametres/modals/AddUserModal";
import AddFranchiseOwnerModal from "@/components/parametres/modals/AddFranchiseOwnerModal";
import AddRestaurantManagerModal from "@/components/parametres/modals/AddRestaurantManagerModal";
import { useAppSelector } from "@/redux/features/hooks";
import { getLogoUrl } from "@/lib/utils";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import { usePermissions } from "@/hooks/usePermissions";
import { useCanAdministerRoles } from "@/hooks/useCanAdministerRoles";
import { Roles } from "@/redux/services/user/user.type";
import { useUpdateRestaurantLogo } from "@/hooks/parametres/useEditRestaurant";
import ListeRestaurants from "@/components/parametres/tabs/ListeRestaurants";
import ListeAccessRoles from "@/components/parametres/tabs/ListeAccessRoles";
import Integrations from "@/components/parametres/tabs/Integrations";
import { hasVisibleIntegrations } from "@/lib/features";
import AddRestaurantModal from "@/components/parametres/modals/AddRestaurantModal";
import {
  Building2,
  Users,
  UserCog,
  Store,
  Info,
  ShieldCheck,
  Plug,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Tab {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
}

const Settings = () => {
  const {
    currentUser,
    currentRestaurant: restaurant,
    currentFranchise,
  } = useAppSelector((state) => state.user);
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  // A real back-office admin reaches every settings tab. A pure permission
  // delegate (ROLE_STAFF holding only settings.view/manage) is limited to the
  // restaurant profile — manager roster, employees and access roles stay
  // role-gated.
  const isManagerRole = currentUser?.roles?.includes(Roles.manager) ?? false;
  const { canManage, canView } = usePermissions();
  const canManageSettings = canManage("settings");
  // A pure delegate (no manager/owner role) may still hold employees.view,
  // which lets them see the staff list read-only (create/PIN/edit stay
  // manager-gated).
  const canViewEmployees = canView("employees");
  // Only primary managers (and owners/support) administer access roles.
  const canAdministerRoles = useCanAdministerRoles();

  const { isOpen, open, close } = useToggle();
  const {
    isOpen: isOpenRestaurant,
    open: openRestaurant,
    close: closeRestaurant,
  } = useToggle();
  const {
    isOpen: isOpenOwner,
    open: openOwner,
    close: closeOwner,
  } = useToggle();
  const {
    isOpen: isOpenManager,
    open: openManager,
    close: closeManager,
  } = useToggle();
  // Restaurant just created by the owner: preselected in the manager modal
  // that opens right after (restaurants start without a manager, SFAPI-214).
  const [newRestaurantId, setNewRestaurantId] = useState<string | undefined>();
  const closeManagerModal = () => {
    setNewRestaurantId(undefined);
    closeManager();
  };

  const { handleUpdateLogo, avatar } = useUpdateRestaurantLogo(restaurant);

  // Any manager may manage the roster (the API refuses mutations that touch
  // the PRIMARY manager, and the list hides those actions on the primary row).
  const canManageManagers = isOwner || isManagerRole;

  // The roles tab is only available to role administrators.
  const rolesTab: Tab[] = canAdministerRoles
    ? [{ id: "roles", label: "Rôles & permissions", icon: ShieldCheck }]
    : [];

  // Hidden while no integration is enabled (WhatsApp: SFRESTO-154).
  const integrationsTab: Tab[] = hasVisibleIntegrations()
    ? [{ id: "integrations", label: "Intégrations", icon: Plug }]
    : [];

  const ownerTabs: Tab[] = [
    { id: "franchise-info", label: "Franchise", icon: Building2 },
    { id: "owners", label: "Propriétaires", icon: UserCog },
    { id: "restaurants", label: "Restaurants", icon: Store },
    { id: "managers", label: "Managers", icon: Users },
    { id: "users", label: "Employés", icon: Users },
    ...rolesTab,
    ...integrationsTab,
  ];

  const managerTabs: Tab[] = [
    { id: "restaurant-info", label: "Restaurant", icon: Info },
    { id: "managers", label: "Managers", icon: Users },
    { id: "users", label: "Utilisateurs", icon: Users },
    ...rolesTab,
    ...integrationsTab,
  ];

  // Settings delegates (no manager/owner role) manage the restaurant profile,
  // plus the staff list if they hold employees.view.
  const delegateTabs: Tab[] = [
    { id: "restaurant-info", label: "Restaurant", icon: Info },
    ...(canViewEmployees
      ? [{ id: "users", label: "Utilisateurs", icon: Users }]
      : []),
  ];

  const tabs = isOwner ? ownerTabs : isManagerRole ? managerTabs : delegateTabs;
  const [activeTabId, setActiveTabId] = useState<string>(tabs[0].id);

  const tabTitles: Record<string, string> = {
    "franchise-info": "Informations de la franchise",
    "restaurant-info": "Informations du restaurant",
    owners: "Liste des propriétaires",
    managers: "Liste des managers",
    users: isOwner ? "Liste des employés" : "Liste des utilisateurs",
    restaurants: "Liste des restaurants",
    integrations: "Intégrations",
  };

  const addButtons: Record<string, { label: string; onClick: () => void }> = {
    owners: { label: "Ajouter un propriétaire", onClick: openOwner },
    ...(canManageManagers
      ? { managers: { label: "Ajouter un manager", onClick: openManager } }
      : {}),
    // User creation stays manager/owner-role gated (not delegatable).
    ...(isOwner || isManagerRole
      ? { users: { label: "Ajouter un utilisateur", onClick: open } }
      : {}),
    restaurants: { label: "Ajouter un restaurant", onClick: openRestaurant },
  };

  const addButton = addButtons[activeTabId];

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.owner]}
        allowedPermissions={["settings.view"]}
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <PageTitle
              title="Paramètres"
              description="Configurez votre restaurant et gérez votre équipe"
            />
            {!isOwner && (
              <div className="flex items-center gap-3">
                {canManageSettings ? (
                  <EditableAvatar
                    imageUrl={getLogoUrl(restaurant?.image?.url)}
                    alt="Logo restaurant"
                    id="logo"
                    avatar={avatar}
                    onChange={handleUpdateLogo}
                    size={100}
                  />
                ) : (
                  <img
                    src={getLogoUrl(restaurant?.image?.url)}
                    alt="Logo restaurant"
                    className="h-[100px] w-[100px] rounded-full object-cover"
                  />
                )}
                <div className="hidden sm:block">
                  <p className="font-semibold text-card-foreground">
                    {restaurant?.name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {restaurant?.address}
                  </p>
                </div>
              </div>
            )}
            {isOwner && (
              <p className="font-semibold text-card-foreground">
                {currentFranchise?.name}
              </p>
            )}
          </div>

          {/* Horizontal tabs */}
          <div className="border-b border-border overflow-x-auto scroll-hidden">
            <nav className="flex gap-1 min-w-max">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTabId(id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap",
                    activeTabId === id
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-card-foreground hover:border-border"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content area */}
          <div className="bg-card rounded-lg border border-border p-6">
            {addButton && (
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-card-foreground">
                  {tabTitles[activeTabId]}
                </h3>
                <ButtonAdd
                  label={addButton.label}
                  type="button"
                  onClick={addButton.onClick}
                />
              </div>
            )}

            {activeTabId === "restaurant-info" && (
              <InfoRestaurant item={restaurant} canManage={canManageSettings} />
            )}
            {activeTabId === "franchise-info" && (
              <InfoFranchise item={currentFranchise} />
            )}
            {activeTabId === "owners" && <ListeFranchiseOwners />}
            {activeTabId === "managers" && <ListeRestaurantManagers />}
            {activeTabId === "users" && <ListeUtilisateurs />}
            {activeTabId === "restaurants" && <ListeRestaurants />}
            {activeTabId === "roles" && <ListeAccessRoles />}
            {activeTabId === "integrations" && <Integrations />}
          </div>
        </div>

        {isOpen && <AddUserModal isOpen={isOpen} onClose={close} />}
        {isOpenRestaurant && (
          <AddRestaurantModal
            isOpen={isOpenRestaurant}
            onClose={closeRestaurant}
            onCreated={(created) => {
              setNewRestaurantId(created.id);
              openManager();
            }}
          />
        )}
        {isOpenOwner && (
          <AddFranchiseOwnerModal isOpen={isOpenOwner} onClose={closeOwner} />
        )}
        {isOpenManager && (
          <AddRestaurantManagerModal
            isOpen={isOpenManager}
            onClose={closeManagerModal}
            restaurantId={newRestaurantId}
          />
        )}
      </RoleProtectedPage>
    </DashLayout>
  );
};

export default Settings;
